import { zodResolver } from '@hookform/resolvers/zod';
import {
  type PersonaDto,
  Platform,
  type SocialAccountDto,
} from '@reelplicate/api-client/model';
import { getListPersonasQueryKey } from '@reelplicate/api-client/personas';
import {
  getListSocialAccountsQueryKey,
  useCreateSocialAccount,
  useUpdateSocialAccount,
} from '@reelplicate/api-client/social-accounts';
import { createSocialAccountBodyHandleMax } from '@reelplicate/api-client/zod/social-accounts';
import {
  Button,
  Field,
  Modal,
  ModalClose,
  ModalContent,
  Select,
  SelectItem,
  Stack,
  TextInput,
  toast,
} from '@reelplicate/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useId } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';

import { PLATFORM_LABELS } from './platforms';

/** The owner Select's value for the brand itself (personas use their id). */
/** How each platform's account is entered - the form its stats are read by. */
const HANDLE_HINTS: Record<Platform, string> = {
  TIKTOK: '@handle or a profile link.',
  INSTAGRAM: '@handle or a profile link.',
  YOUTUBE: 'The channel’s @handle or link.',
  FACEBOOK: 'The page’s link.',
  LINKEDIN: 'The page or profile name.',
};

const BRAND = 'brand';

const AccountSchema = z.object({
  platform: z.enum(Platform),
  handle: z
    .string()
    .trim()
    .min(1, 'Enter the account’s handle')
    .max(
      createSocialAccountBodyHandleMax,
      `Keep it under ${createSocialAccountBodyHandleMax} characters`,
    ),
  owner: z.string(),
});
type AccountValues = z.infer<typeof AccountSchema>;

const toValues = (account?: SocialAccountDto): AccountValues => ({
  platform: account?.platform ?? 'TIKTOK',
  handle: account?.handle ?? '',
  owner: account?.personaId ?? BRAND,
});

/**
 * Add an account by hand, or change an existing one's handle or owner (the
 * brand or a persona). The platform is fixed once added. Real connections
 * (OAuth, sync) replace this with the publishing integrations.
 */
export function SocialAccountModal({
  workspaceId,
  brandName,
  personas,
  account,
  open,
  onOpenChange,
}: {
  workspaceId: string;
  brandName: string;
  personas: PersonaDto[];
  /** The account to edit; omit to add one. */
  account?: SocialAccountDto;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const formId = useId();
  const queryClient = useQueryClient();
  const create = useCreateSocialAccount();
  const update = useUpdateSocialAccount();
  const form = useForm<AccountValues>({
    resolver: zodResolver(AccountSchema),
    defaultValues: toValues(account),
  });

  useEffect(() => {
    if (open) form.reset(toValues(account));
  }, [open, account, form]);
  const platform = useWatch({ control: form.control, name: 'platform' });

  const onSubmit = form.handleSubmit(async ({ platform, handle, owner }) => {
    const personaId = owner === BRAND ? null : owner;
    try {
      if (account) {
        await update.mutateAsync({
          id: workspaceId,
          accountId: account.id,
          data: { handle, personaId },
        });
      } else {
        await create.mutateAsync({
          id: workspaceId,
          data: { platform, handle, personaId },
        });
      }
    } catch {
      return; // The failure is already shown as a toast.
    }
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: getListSocialAccountsQueryKey(workspaceId),
      }),
      queryClient.invalidateQueries({ queryKey: getListPersonasQueryKey(workspaceId) }),
    ]);
    toast.success(account ? 'Account saved' : `${handle} added`);
    onOpenChange(false);
  });

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalContent
        size="sm"
        title={account ? `Edit ${account.handle}` : 'Add account'}
        description="Pick the platform and who it posts for. Syncing and publishing arrive with platform connections."
        footer={
          <>
            <ModalClose asChild>
              <Button variant="ghost">Cancel</Button>
            </ModalClose>
            <Button
              type="submit"
              form={formId}
              loading={create.isPending || update.isPending}
            >
              {account ? 'Save account' : 'Add account'}
            </Button>
          </>
        }
      >
        <Stack
          as="form"
          id={formId}
          gap={4}
          onSubmit={(e) => void onSubmit(e)}
          noValidate
        >
          <Controller
            name="platform"
            control={form.control}
            render={({ field }) => (
              <Field label="Platform">
                <Select
                  value={field.value}
                  onValueChange={field.onChange}
                  disabled={Boolean(account)}
                >
                  {Object.values(Platform).map((platform) => (
                    <SelectItem key={platform} value={platform}>
                      {PLATFORM_LABELS[platform]}
                    </SelectItem>
                  ))}
                </Select>
              </Field>
            )}
          />
          <Controller
            name="handle"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field
                label="Handle"
                helper={HANDLE_HINTS[platform]}
                error={fieldState.error?.message}
              >
                <TextInput
                  {...field}
                  placeholder={
                    platform === 'FACEBOOK' ? 'facebook.com/oatandember' : '@oatandember'
                  }
                  autoComplete="off"
                />
              </Field>
            )}
          />
          <Controller
            name="owner"
            control={form.control}
            render={({ field }) => (
              <Field label="Posts for">
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectItem value={BRAND}>{brandName} (brand)</SelectItem>
                  {personas.map((persona) => (
                    <SelectItem key={persona.id} value={persona.id}>
                      {persona.name}
                    </SelectItem>
                  ))}
                </Select>
              </Field>
            )}
          />
        </Stack>
      </ModalContent>
    </Modal>
  );
}
